"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { RefObject, useEffect, useRef, useState } from "react";
import { TextShimmer } from "@/components/ui/text-shimmer";

interface StepListDemoProps {
  stepRef: RefObject<HTMLDivElement>;
  onShowVideoChange?: (show: boolean) => void;
  onDownloadClick?: () => void;
}

export function StepListDemo({
  stepRef,
  onShowVideoChange,
  onDownloadClick,
}: StepListDemoProps) {
  const { scrollYProgress } = useScroll({
    target: stepRef,
    offset: ["start center", "end end"],
  });

  // 视频播放状态
  const videoRef = useRef<HTMLVideoElement>(null);


  // 基于滚动进度控制视频显示 - 第二阶段 (0.7-1.0, 占总进度30%)
  const showVideo = useTransform(scrollYProgress, [0.7, 0.75, 0.9, 1.0], [0, 1, 1, 1]);
  const [videoPlaying, setVideoPlaying] = useState(false);


  // 视频播放控制 - 基于 showVideo motion value
  useEffect(() => {
    const unsubscribe = showVideo.on("change", (value) => {
      const shouldPlay = value > 0;
      
      if (videoRef.current) {
        if (shouldPlay && !videoPlaying) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
          setVideoPlaying(true);
        } else if (!shouldPlay && videoPlaying) {
          videoRef.current.pause();
          setVideoPlaying(false);
        }
      }

      // 通知父组件视频状态变化
      if (onShowVideoChange) {
        onShowVideoChange(shouldPlay);
      }
    });

    return unsubscribe;
  }, [showVideo, onShowVideoChange, videoPlaying]);

  // 文本覆盖层控制已转移到父组件 StepListSection

  const steps = [
    { text: "Search for trending technology influencers", id: 1 },
    { text: "Filter by engagement metrics and reach", id: 2 },
    { text: "Analyze content and influence areas", id: 3 },
    { text: "Compile top 10 KOLs with statistics", id: 4 },
    { text: "Generate comprehensive report", id: 5 },
    { text: "Cross-platform data validation", id: 6 },
  ];

  // User bubble animation (0-0.2 of scroll) - 扩展时间
  const userBubbleX = useTransform(scrollYProgress, [0, 0.2], [200, 0]);
  const userBubbleOpacity = useTransform(scrollYProgress, [0, 0.1, 0.2], [0, 1, 1]);

  // Skip AI bubble, go directly to steps (0.2-0.4 of scroll) - 扩展时间
  const stepsOpacity = useTransform(scrollYProgress, [0.2, 0.4], [0, 1]);

  // 6个步骤的出现动画 - 从左侧(-200)开始，从上到下依次X轴动画 (0.2-0.44) - 扩展时间
  const step1AppearX = useTransform(scrollYProgress, [0.2, 0.24], [-200, 0]);
  const step2AppearX = useTransform(scrollYProgress, [0.24, 0.28], [-200, 0]);
  const step3AppearX = useTransform(scrollYProgress, [0.28, 0.32], [-200, 0]);
  const step4AppearX = useTransform(scrollYProgress, [0.32, 0.36], [-200, 0]);
  const step5AppearX = useTransform(scrollYProgress, [0.36, 0.4], [-200, 0]);
  const step6AppearX = useTransform(scrollYProgress, [0.4, 0.44], [-200, 0]);

  // 对应的透明度动画
  const step1AppearOpacity = useTransform(scrollYProgress, [0.2, 0.24], [0, 1]);
  const step2AppearOpacity = useTransform(scrollYProgress, [0.24, 0.28], [0, 1]);
  const step3AppearOpacity = useTransform(scrollYProgress, [0.28, 0.32], [0, 1]);
  const step4AppearOpacity = useTransform(scrollYProgress, [0.32, 0.36], [0, 1]);
  const step5AppearOpacity = useTransform(scrollYProgress, [0.36, 0.4], [0, 1]);
  const step6AppearOpacity = useTransform(scrollYProgress, [0.4, 0.44], [0, 1]);

  const stepAppearXTransforms = [step1AppearX, step2AppearX, step3AppearX, step4AppearX, step5AppearX, step6AppearX];

  // Individual step exit animations - 从右侧移动出去
  // 第6个先移出，然后5、4、3、2、1依次从右侧移出 (0.5-0.7)
  const step6ExitX = useTransform(scrollYProgress, [0.5, 0.53], [0, 200]);
  const step5ExitX = useTransform(scrollYProgress, [0.53, 0.56], [0, 200]);
  const step4ExitX = useTransform(scrollYProgress, [0.56, 0.59], [0, 200]);
  const step3ExitX = useTransform(scrollYProgress, [0.59, 0.62], [0, 200]);
  const step2ExitX = useTransform(scrollYProgress, [0.62, 0.65], [0, 200]);
  const step1ExitX = useTransform(scrollYProgress, [0.65, 0.68], [0, 200]);

  // 透明度动画 - 配合右移效果
  const step6ExitOpacity = useTransform(scrollYProgress, [0.5, 0.53], [1, 0]);
  const step5ExitOpacity = useTransform(scrollYProgress, [0.53, 0.56], [1, 0]);
  const step4ExitOpacity = useTransform(scrollYProgress, [0.56, 0.59], [1, 0]);
  const step3ExitOpacity = useTransform(scrollYProgress, [0.59, 0.62], [1, 0]);
  const step2ExitOpacity = useTransform(scrollYProgress, [0.62, 0.65], [1, 0]);
  const step1ExitOpacity = useTransform(scrollYProgress, [0.65, 0.68], [1, 0]);

  const stepExitXTransforms = [step1ExitX, step2ExitX, step3ExitX, step4ExitX, step5ExitX, step6ExitX];

  // 组合X轴变换 - 将入场和退场的X轴动画合并
  const combinedX1 = useTransform([step1AppearX, step1ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX2 = useTransform([step2AppearX, step2ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX3 = useTransform([step3AppearX, step3ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX4 = useTransform([step4AppearX, step4ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX5 = useTransform([step5AppearX, step5ExitX], ([appear, exit]) => (appear as number) + (exit as number));
  const combinedX6 = useTransform([step6AppearX, step6ExitX], ([appear, exit]) => (appear as number) + (exit as number));

  const combinedXTransforms = [combinedX1, combinedX2, combinedX3, combinedX4, combinedX5, combinedX6];

  // 组合透明度变换 - 在顶层创建，避免在 map 中使用 hooks
  const combinedOpacity1 = useTransform([step1AppearOpacity, step1ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity2 = useTransform([step2AppearOpacity, step2ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity3 = useTransform([step3AppearOpacity, step3ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity4 = useTransform([step4AppearOpacity, step4ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity5 = useTransform([step5AppearOpacity, step5ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedOpacity6 = useTransform([step6AppearOpacity, step6ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );

  const combinedOpacityTransforms = [
    combinedOpacity1,
    combinedOpacity2,
    combinedOpacity3,
    combinedOpacity4,
    combinedOpacity5,
    combinedOpacity6,
  ];

  return (
    <div className="w-full relative flex items-center justify-center">
        {/* 视频容器 - 基于滚动进度显示 (第二阶段) */}
        <motion.div
          className="absolute z-20 flex items-center justify-center w-[600px] h-[450px]"
          style={{ 
            opacity: showVideo,
            x: useTransform(showVideo, [0, 1], [-100, 0]),
            filter: useTransform(showVideo, [0, 1], ["blur(4px)", "blur(0px)"])
          }}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-2xl"
            src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Step+List.webm"
            autoPlay
            loop
            muted
            playsInline
          />
        </motion.div>
        {/* Wireframe container - 在第二阶段时逐渐隐藏 */}
        <motion.div
          className="sticky top-0 flex flex-col justify-center rounded-2xl w-[600px] h-[450px]"
          style={{ 
            opacity: useTransform(showVideo, [0, 0.1, 0.5, 1], [1, 1, 0.3, 0])
          }}
        >
          {/* User Message - Right Side */}
          <motion.div
            className="flex justify-end mb-8"
            style={{
              x: userBubbleX,
              opacity: userBubbleOpacity,
            }}
          >
            <div className="w-[180px] h-[60px] bg-white/10 border border-white/10 rounded-2xl flex items-center px-4">
              <div className="space-y-2 w-full">
                <div className="h-2 bg-white/10 rounded w-3/4"></div>
                <div className="h-1.5 bg-white/10 rounded w-1/2"></div>
                <div className="h-1.5 bg-white/10 rounded w-2/3"></div>
              </div>
            </div>
          </motion.div>

          {/* Individual Steps - appear and sink down */}
          <motion.div
            className="flex flex-col gap-2 bg-white/10 border border-white/10 rounded-2xl p-4"
            style={{ opacity: stepsOpacity }}
            id="box"
          >
            <TextShimmer className="font-mono text-sm " duration={3}>
              Thinking ...
            </TextShimmer>
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                className="w-full max-w-[550px] mx-auto"
                style={{
                  x: combinedXTransforms[index],
                  opacity: combinedOpacityTransforms[index],
                }}
              >
                <div
                  className={`
                w-[550px] h-[55px] bg-white/10
                border border-white/10
                hover:border-white/10 transition-colors
                ${index === 0 ? "rounded-t-2xl rounded-b-lg" : ""}
                ${index === steps.length - 1 ? "rounded-b-2xl rounded-t-lg" : ""}
                ${index !== 0 && index !== steps.length - 1 ? "rounded-lg" : ""}
                flex items-center px-3 py-2
              `}
                >
                  {/* Step Indicator */}
                  <div className="w-3 h-3 bg-white/10 rounded-full mr-3 flex-shrink-0"></div>

                  {/* Content Lines */}
                  <div className="flex-1 space-y-1">
                    <div className="h-1.5 bg-white/10 rounded w-3/4"></div>
                    <div className="h-1 bg-white/10 rounded w-1/2"></div>
                  </div>

                  {/* Status Indicator */}
                  <div className="w-2 h-2 bg-white/10 rounded-full flex-shrink-0"></div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute top-4 right-4 w-16 h-16 border border-white/10 rounded-full"></div>
            <div className="absolute bottom-8 left-8 w-8 h-8 border border-white/10 rounded"></div>
            <div className="absolute top-1/2 left-4 w-4 h-4 border border-white/10 rounded-full"></div>
            <div className="absolute top-1/3 right-1/4 w-6 h-6 border border-white/10 rounded"></div>
          </div>
        </motion.div>
      </div>
  );
}
